import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Info, AlertTriangle, MapPin, Navigation, ArrowRight, Hand } from 'lucide-react';
import './structure-move-dialog.css';
const MAX_DISTANCE = 50; // metres - must match backend validation
export const StructureMoveDialog = ({ open, onClose, feature, map, onSuccess, onDragModeStart, onDragModeEnd, }) => {
    const [mode, setMode] = useState(null);
    const [isMoving, setIsMoving] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    // Original position
    const originalPos = {
        easting: parseFloat(feature?.properties?.EASTING || 0),
        northing: parseFloat(feature?.properties?.NORTHING || 0),
    };
    // New position (initialized to original)
    const [newPos, setNewPos] = useState(originalPos);
    // Offset values for offset mode
    const [offsetEasting, setOffsetEasting] = useState('0');
    const [offsetNorthing, setOffsetNorthing] = useState('0');
    // Manual coordinate values
    const [manualEasting, setManualEasting] = useState(originalPos.easting.toString());
    const [manualNorthing, setManualNorthing] = useState(originalPos.northing.toString());
    // Refs for Leaflet elements
    const previewMarkerRef = useRef(null);
    const originalMarkerRef = useRef(null);
    const moveLineRef = useRef(null);
    // Calculate distance moved
    const distance = Math.sqrt(Math.pow(newPos.easting - originalPos.easting, 2) +
        Math.pow(newPos.northing - originalPos.northing, 2));
    // Get distance status
    const getDistanceStatus = (dist) => {
        if (dist <= 25)
            return 'safe';
        if (dist <= 40)
            return 'warning';
        if (dist <= MAX_DISTANCE)
            return 'danger';
        return 'exceeded';
    };
    const distanceStatus = getDistanceStatus(distance);
    // Handle drag mode transitions
    useEffect(() => {
        if (!open) {
            // Dialog closed - restore popup if it was hidden
            if (onDragModeEnd) {
                onDragModeEnd();
            }
        }
        else if (mode !== 'drag' && onDragModeEnd) {
            // Switched away from drag mode - restore popup
            onDragModeEnd();
        }
    }, [open, mode, onDragModeEnd]);
    // Store the actual original lat/lng to avoid conversion discrepancies
    const [originalLatLng, setOriginalLatLng] = useState(null);
    // Store the correction offset between actual position and proj4 conversion
    const correctionOffsetRef = useRef(null);
    // Store the actual dragged lat/lng position to avoid conversion errors
    const [draggedLatLng, setDraggedLatLng] = useState(null);
    // Initialize markers when dialog opens and drag mode is selected
    useEffect(() => {
        if (!open || !map || !feature || !originalLatLng)
            return;
        // Only initialize markers when drag mode is selected
        if (mode !== 'drag')
            return;
        const L = window.L;
        if (!L)
            return;
        // Close the underlying popup when in drag mode
        if (onDragModeStart) {
            onDragModeStart();
        }
        console.log('[StructureMoveDialog] Initializing markers with originalLatLng:', originalLatLng);
        // Create original position marker (ghost)
        const ghostIcon = L.divIcon({
            className: 'structure-move-ghost-marker',
            html: '<div class="ghost-marker-inner"></div>',
            iconSize: [20, 20],
            iconAnchor: [10, 10],
        });
        originalMarkerRef.current = L.marker(originalLatLng, {
            icon: ghostIcon,
            interactive: false,
            zIndexOffset: -1000,
        }).addTo(map);
        // Create preview marker (draggable)
        const previewIcon = L.divIcon({
            className: 'structure-move-preview-marker',
            html: '<div class="preview-marker-inner"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="5 9 2 12 5 15"/><polyline points="9 5 12 2 15 5"/><polyline points="15 19 12 22 9 19"/><polyline points="19 9 22 12 19 15"/><line x1="2" y1="12" x2="22" y2="12"/><line x1="12" y1="2" x2="12" y2="22"/></svg></div>',
            iconSize: [30, 30],
            iconAnchor: [15, 15],
        });
        previewMarkerRef.current = L.marker(originalLatLng, {
            icon: previewIcon,
            draggable: mode === 'drag',
            zIndexOffset: 1000,
        }).addTo(map);
        // Create line connecting original and new position
        moveLineRef.current = L.polyline([originalLatLng, originalLatLng], {
            color: '#3b82f6',
            weight: 2,
            opacity: 0.6,
            dashArray: '5, 10',
            interactive: false,
        }).addTo(map);
        // Handle drag events
        if (mode === 'drag') {
            previewMarkerRef.current.on('dragstart', (e) => {
                setIsDragging(true);
                setError(null);
                // Log the initial drag position
                const startLatLng = e.target.getLatLng();
                console.log('[StructureMoveDialog] Drag started at:', startLatLng);
                // Check if there's an offset between marker position and mouse position
                if (e.originalEvent) {
                    const containerPoint = map.latLngToContainerPoint(startLatLng);
                    console.log('[StructureMoveDialog] Mouse position:', e.originalEvent.clientX, e.originalEvent.clientY);
                    console.log('[StructureMoveDialog] Marker container position:', containerPoint);
                }
            });
            previewMarkerRef.current.on('drag', (e) => {
                const latLng = e.target.getLatLng();
                // Debug: Log every 10th drag event to avoid spam
                if (Math.random() < 0.1) {
                    console.log('[StructureMoveDialog] Dragging to:', latLng);
                }
                // Store the actual dragged position
                setDraggedLatLng([latLng.lat, latLng.lng]);
                let bng = convertLatLngToBNG(latLng.lat, latLng.lng);
                // Apply the OSTN15 correction offset if we have it
                if (correctionOffsetRef.current) {
                    bng.easting += correctionOffsetRef.current.eastingOffset;
                    bng.northing += correctionOffsetRef.current.northingOffset;
                }
                setNewPos(bng);
                // Update line
                moveLineRef.current.setLatLngs([originalLatLng, latLng]);
            });
            previewMarkerRef.current.on('dragend', (e) => {
                const finalLatLng = e.target.getLatLng();
                console.log('[StructureMoveDialog] Drag ended at:', finalLatLng);
                // Store the final dragged position
                setDraggedLatLng([finalLatLng.lat, finalLatLng.lng]);
                setIsDragging(false);
            });
        }
        // DON'T auto-pan the map - let user see current view
        // map.setView(originalLatLng, Math.max(map.getZoom(), 18));
        // Cleanup
        return () => {
            if (originalMarkerRef.current)
                map.removeLayer(originalMarkerRef.current);
            if (previewMarkerRef.current)
                map.removeLayer(previewMarkerRef.current);
            if (moveLineRef.current)
                map.removeLayer(moveLineRef.current);
        };
    }, [open, map, feature, mode, originalLatLng]);
    // Initialize the original lat/lng once when dialog opens
    useEffect(() => {
        if (!open || !feature || originalLatLng)
            return;
        let initialLatLng;
        console.log('[StructureMoveDialog] Initializing original position:', {
            geometry: feature.geometry,
            easting: originalPos.easting,
            northing: originalPos.northing,
            properties: feature.properties
        });
        // ONLY use the geometry coordinates - these are accurate!
        if (feature.geometry && feature.geometry.type === 'Point' && feature.geometry.coordinates) {
            // GeoJSON Point coordinates are [lng, lat]
            const [lng, lat] = feature.geometry.coordinates;
            initialLatLng = [lat, lng];
            console.log(`[StructureMoveDialog] ✓ Using ACCURATE Point geometry: lat=${lat}, lng=${lng}`);
        }
        else {
            // This should never happen for structures, but log an error if it does
            console.error('[StructureMoveDialog] ERROR: No Point geometry found! Feature:', feature);
            // As an absolute last resort, try to get something
            if (feature.properties?.lat && feature.properties?.lng) {
                initialLatLng = [feature.properties.lat, feature.properties.lng];
                console.warn('[StructureMoveDialog] Using fallback lat/lng from properties');
            }
            else {
                // This will be inaccurate but at least something will show
                console.error('[StructureMoveDialog] CRITICAL: No usable coordinates found!');
                initialLatLng = [52.2, 0.15]; // Approximate Cambridge area
            }
        }
        setOriginalLatLng(initialLatLng);
        // Calculate the correction offset if we have point geometry
        if (feature.geometry && feature.geometry.type === 'Point') {
            const uncorrectedBNG = convertLatLngToBNG(initialLatLng[0], initialLatLng[1]);
            const eastingOffset = originalPos.easting - uncorrectedBNG.easting;
            const northingOffset = originalPos.northing - uncorrectedBNG.northing;
            correctionOffsetRef.current = { eastingOffset, northingOffset };
            console.log(`[StructureMoveDialog] Calculated OSTN15 correction offset: E=${eastingOffset.toFixed(3)}m, N=${northingOffset.toFixed(3)}m`);
        }
    }, [open, feature]);
    // Update marker position when mode changes
    useEffect(() => {
        if (!previewMarkerRef.current || !map || !originalLatLng)
            return;
        // IMPORTANT: Don't update marker position while actively dragging!
        // The drag event handler already positions the marker correctly
        if (isDragging) {
            console.log('[StructureMoveDialog] Skipping marker update during drag');
            return;
        }
        const L = window.L;
        // Use the actual dragged position if we have it (from drag mode)
        // For offset/coordinate modes, we calculate new position but need proper conversion
        let newLatLng;
        if (mode === 'drag' && draggedLatLng) {
            newLatLng = draggedLatLng;
            console.log('[StructureMoveDialog] Using actual dragged position:', newLatLng);
        }
        else if (mode === 'offset' || mode === 'coordinate') {
            // For offset/coordinate modes, calculate the offset from original
            const eastingDiff = newPos.easting - originalPos.easting;
            const northingDiff = newPos.northing - originalPos.northing;
            // Apply the offset in lat/lng space (approximate but good enough for small distances)
            // 1 degree latitude ≈ 111km, 1 degree longitude ≈ 70km at UK latitudes
            const latDiff = northingDiff / 111000;
            const lngDiff = eastingDiff / 70000;
            newLatLng = [
                originalLatLng[0] + latDiff,
                originalLatLng[1] + lngDiff
            ];
            console.log(`[StructureMoveDialog] Calculated position from offset: (${eastingDiff}m E, ${northingDiff}m N)`);
        }
        else {
            // For drag mode before any dragging, use original position
            newLatLng = originalLatLng;
        }
        previewMarkerRef.current.setLatLng(newLatLng);
        previewMarkerRef.current.dragging?.[mode === 'drag' ? 'enable' : 'disable']();
        if (moveLineRef.current) {
            moveLineRef.current.setLatLngs([originalLatLng, newLatLng]);
        }
        // Update line colour based on distance
        if (moveLineRef.current) {
            const colour = distanceStatus === 'safe' ? '#10b981' :
                distanceStatus === 'warning' ? '#f59e0b' :
                    distanceStatus === 'danger' ? '#ef4444' :
                        '#991b1b';
            moveLineRef.current.setStyle({ color: colour });
        }
    }, [newPos, mode, distanceStatus, isDragging, draggedLatLng]);
    // Handle offset mode changes
    useEffect(() => {
        if (mode === 'offset') {
            const eastingOffset = parseFloat(offsetEasting) || 0;
            const northingOffset = parseFloat(offsetNorthing) || 0;
            setNewPos({
                easting: originalPos.easting + eastingOffset,
                northing: originalPos.northing + northingOffset,
            });
        }
    }, [mode, offsetEasting, offsetNorthing]);
    // Handle coordinate mode changes
    useEffect(() => {
        if (mode === 'coordinate') {
            const easting = parseFloat(manualEasting);
            const northing = parseFloat(manualNorthing);
            if (!isNaN(easting) && !isNaN(northing)) {
                setNewPos({ easting, northing });
            }
        }
    }, [mode, manualEasting, manualNorthing]);
    // Reset to original position
    const handleReset = () => {
        setNewPos(originalPos);
        setOffsetEasting('0');
        setOffsetNorthing('0');
        setManualEasting(originalPos.easting.toString());
        setManualNorthing(originalPos.northing.toString());
        setError(null);
    };
    // Submit move
    const handleSubmit = async () => {
        setError(null);
        // Validate distance
        if (distance > MAX_DISTANCE) {
            setError(`Cannot move structure more than ${MAX_DISTANCE}m. Current distance: ${distance.toFixed(2)}m`);
            return;
        }
        if (distance === 0) {
            setError('Structure has not been moved');
            return;
        }
        setIsMoving(true);
        try {
            const response = await fetch('/structure/move', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({
                    id: feature.properties.DB_ID,
                    easting: newPos.easting.toString(),
                    northing: newPos.northing.toString(),
                }),
            });
            const result = await response.json();
            if (!response.ok || result.errors) {
                const errorMsg = result.errors
                    ? Object.values(result.errors).flat().join(', ')
                    : result.error || 'Failed to move structure';
                throw new Error(errorMsg);
            }
            setSuccess(true);
            // Refresh map layers
            if (window.CFN?.getCachedLayer) {
                const structureLayer = window.CFN.getCachedLayer('StructureLayer');
                const conduitLayer = window.CFN.getCachedLayer('ConduitLayer');
                if (structureLayer)
                    structureLayer.setParams({ time: Date.now() }, false);
                if (conduitLayer)
                    conduitLayer.setParams({ time: Date.now() }, false);
            }
            // Call success callback
            if (onSuccess)
                onSuccess();
            // Close after short delay
            setTimeout(() => {
                onClose();
            }, 1500);
        }
        catch (err) {
            setError(err.message || 'Failed to move structure');
        }
        finally {
            setIsMoving(false);
        }
    };
    const structureTitle = feature?.properties?.DP_IDENTIFIER
        ? `DP${feature.properties.DP_IDENTIFIER}${feature.properties.SUB_POLE !== '00' ? '-' + feature.properties.SUB_POLE : ''}`
        : `Structure ${feature?.properties?.OBJECTID || ''}`;
    // In drag mode, render a minimal floating UI using a Portal
    // Only show this if drag mode is explicitly selected
    if (open && mode === 'drag') {
        // Render the drag panel using a portal to the document body
        // This ensures it's not destroyed when the popup is hidden
        return ReactDOM.createPortal(_jsxs("div", { className: "structure-move-drag-panel", children: [_jsxs("div", { className: "drag-panel-header", children: [_jsxs("h3", { className: "drag-panel-title", children: [_jsx(MapPin, { className: "inline mr-2 h-4 w-4" }), "Moving: ", structureTitle] }), _jsx("button", { onClick: onClose, className: "drag-panel-close", "aria-label": "Cancel", children: "\u2715" })] }), error && (_jsxs("div", { className: "drag-panel-error", children: [_jsx(AlertTriangle, { className: "h-4 w-4" }), _jsx("span", { children: error })] })), success && (_jsx("div", { className: "drag-panel-success", children: "Structure moved successfully!" })), _jsxs("div", { className: `drag-panel-distance status-${distanceStatus}`, children: [_jsx("div", { className: "distance-bar", children: _jsx("div", { className: "distance-fill", style: { width: `${Math.min((distance / MAX_DISTANCE) * 100, 100)}%` } }) }), _jsxs("div", { className: "distance-text", children: [_jsxs("span", { className: "distance-value", children: [distance.toFixed(2), "m"] }), _jsxs("span", { className: "distance-max", children: ["/ ", MAX_DISTANCE, "m max"] })] })] }), _jsxs("div", { className: "drag-panel-instructions", children: [_jsx(Info, { className: "h-4 w-4" }), _jsx("span", { children: "Drag the blue marker to move the structure" }), isDragging && _jsx("span", { className: "dragging-indicator", children: "Dragging..." })] }), _jsxs("div", { className: "drag-panel-actions", children: [_jsx(Button, { onClick: handleReset, variant: "outline", size: "sm", disabled: isMoving || distance === 0, children: "Reset" }), _jsx(Button, { onClick: handleSubmit, variant: "default", size: "sm", disabled: isMoving || distance === 0 || distance > MAX_DISTANCE, children: isMoving ? 'Moving...' : 'Confirm Move' })] })] }), document.body // Portal target
        );
    }
    return (_jsx(Dialog, { open: open, onOpenChange: (isOpen) => !isOpen && onClose(), children: _jsxs(DialogContent, { className: "structure-move-dialog sm:max-w-[500px]", children: [_jsxs(DialogHeader, { children: [_jsxs(DialogTitle, { children: [_jsx(MapPin, { className: "inline mr-2 h-5 w-5" }), "Move Structure"] }), _jsx(DialogDescription, { className: "text-muted-foreground", children: structureTitle })] }), _jsxs("div", { className: "structure-move-content", children: [success && (_jsx(Alert, { className: "mb-4 bg-green-50 border-green-200", children: _jsx(AlertDescription, { className: "text-green-800", children: "Structure moved successfully!" }) })), error && (_jsxs(Alert, { variant: "destructive", className: "mb-4", children: [_jsx(AlertTriangle, { className: "h-4 w-4" }), _jsx(AlertDescription, { children: error })] })), _jsxs("div", { className: "mode-selector mb-4", children: [_jsxs("label", { className: "text-sm font-medium mb-2 block", children: ["Select Move Method ", _jsx("span", { className: "text-destructive", children: "*" })] }), _jsxs("div", { className: "grid grid-cols-3 gap-2", children: [_jsxs("button", { type: "button", className: `mode-btn ${mode === 'drag' ? 'active' : ''}`, onClick: () => {
                                                setMode('drag');
                                                setDraggedLatLng(null); // Reset dragged position when switching to drag mode
                                            }, children: [_jsx(Hand, { className: "h-4 w-4 mb-1" }), _jsx("span", { children: "Drag" })] }), _jsxs("button", { type: "button", className: `mode-btn ${mode === 'offset' ? 'active' : ''}`, onClick: () => {
                                                setMode('offset');
                                                setDraggedLatLng(null); // Clear dragged position when switching modes
                                            }, children: [_jsx(ArrowRight, { className: "h-4 w-4 mb-1" }), _jsx("span", { children: "Offset" })] }), _jsxs("button", { type: "button", className: `mode-btn ${mode === 'coordinate' ? 'active' : ''}`, onClick: () => {
                                                setMode('coordinate');
                                                setDraggedLatLng(null); // Clear dragged position when switching modes
                                            }, children: [_jsx(Navigation, { className: "h-4 w-4 mb-1" }), _jsx("span", { children: "Coordinates" })] })] })] }), mode && (_jsxs("div", { className: `distance-indicator status-${distanceStatus}`, children: [_jsx("div", { className: "distance-bar", children: _jsx("div", { className: "distance-fill", style: { width: `${Math.min((distance / MAX_DISTANCE) * 100, 100)}%` } }) }), _jsxs("div", { className: "distance-text", children: [_jsxs("span", { className: "distance-value", children: [distance.toFixed(2), "m"] }), _jsxs("span", { className: "distance-max", children: ["/ ", MAX_DISTANCE, "m max"] })] })] })), !mode && (_jsxs(Alert, { className: "mt-4", children: [_jsx(Info, { className: "h-4 w-4" }), _jsx(AlertDescription, { children: "Please select a move method above to continue" })] })), mode && (_jsxs("div", { className: "mode-inputs mt-4", children: [mode === 'drag' && (_jsxs(Alert, { children: [_jsx(Info, { className: "h-4 w-4" }), _jsxs(AlertDescription, { children: ["Drag the blue marker on the map to move the structure", isDragging && _jsx("span", { className: "ml-2 text-blue-600 font-medium", children: "Dragging..." })] })] })), mode === 'offset' && (_jsxs("div", { className: "grid grid-cols-2 gap-4", children: [_jsxs("div", { children: [_jsx("label", { htmlFor: "offset-easting", className: "text-sm font-medium block mb-1", children: "Easting Offset (m)" }), _jsx("input", { id: "offset-easting", type: "number", step: "0.1", value: offsetEasting, onChange: (e) => setOffsetEasting(e.target.value), className: "w-full px-3 py-2 border rounded-md", placeholder: "0.0" })] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "offset-northing", className: "text-sm font-medium block mb-1", children: "Northing Offset (m)" }), _jsx("input", { id: "offset-northing", type: "number", step: "0.1", value: offsetNorthing, onChange: (e) => setOffsetNorthing(e.target.value), className: "w-full px-3 py-2 border rounded-md", placeholder: "0.0" })] })] })), mode === 'coordinate' && (_jsxs("div", { className: "grid grid-cols-2 gap-4", children: [_jsxs("div", { children: [_jsx("label", { htmlFor: "manual-easting", className: "text-sm font-medium block mb-1", children: "Easting" }), _jsx("input", { id: "manual-easting", type: "number", step: "0.01", value: manualEasting, onChange: (e) => setManualEasting(e.target.value), className: "w-full px-3 py-2 border rounded-md" })] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "manual-northing", className: "text-sm font-medium block mb-1", children: "Northing" }), _jsx("input", { id: "manual-northing", type: "number", step: "0.01", value: manualNorthing, onChange: (e) => setManualNorthing(e.target.value), className: "w-full px-3 py-2 border rounded-md" })] })] }))] })), mode && (_jsx("div", { className: "position-summary mt-4 text-xs text-muted-foreground", children: _jsxs("div", { className: "grid grid-cols-2 gap-2", children: [_jsxs("div", { children: [_jsx("span", { className: "font-medium", children: "Original:" }), " ", originalPos.easting.toFixed(2), ", ", originalPos.northing.toFixed(2)] }), _jsxs("div", { children: [_jsx("span", { className: "font-medium", children: "New:" }), " ", newPos.easting.toFixed(2), ", ", newPos.northing.toFixed(2)] })] }) }))] }), _jsxs(DialogFooter, { children: [_jsx(Button, { variant: "outline", onClick: handleReset, disabled: isMoving || distance === 0, children: "Reset" }), _jsx(Button, { variant: "outline", onClick: onClose, disabled: isMoving, children: "Cancel" }), _jsx(Button, { onClick: handleSubmit, disabled: isMoving || distance === 0 || distanceStatus === 'exceeded', children: isMoving ? 'Moving...' : 'Move Structure' })] })] }) }));
};
// Coordinate conversion helpers
// These use the map-coordinates system which applies OSTN15 grid corrections
function convertBNGToLatLng(easting, northing) {
    // First try the map's coordinate converter if available (it has OSTN15 corrections)
    const mapCoordinates = window.mapCoordinates;
    if (mapCoordinates && mapCoordinates.getLatLngFromBNG) {
        try {
            const latLng = mapCoordinates.getLatLngFromBNG(easting, northing);
            console.log(`[StructureMoveDialog] BNG→LatLng: (${easting}, ${northing}) → (${latLng.lat}, ${latLng.lng}) [using mapCoordinates with OSTN15]`);
            return [latLng.lat, latLng.lng];
        }
        catch (e) {
            console.error('[StructureMoveDialog] mapCoordinates conversion failed:', e);
        }
    }
    // Try using the global proj4 library if available (less accurate - no OSTN15)
    const proj4 = window.proj4;
    if (proj4) {
        try {
            const [lng, lat] = proj4('EPSG:27700').inverse([easting, northing]);
            console.log(`[StructureMoveDialog] BNG→LatLng: (${easting}, ${northing}) → (${lat}, ${lng}) [using proj4 - WARNING: No OSTN15 correction]`);
            return [lat, lng];
        }
        catch (e) {
            console.error('[StructureMoveDialog] proj4 conversion failed:', e);
        }
    }
    // Fallback approximate conversion (not accurate!)
    console.warn('[StructureMoveDialog] No accurate conversion available! Using fallback (VERY INACCURATE)');
    const lat = 49 + (northing - 0) / 111000;
    const lng = -8 + (easting - 0) / 71000;
    return [lat, lng];
}
function convertLatLngToBNG(lat, lng) {
    // Try the map's coordinate converter if available (it has OSTN15 corrections)
    const mapCoordinates = window.mapCoordinates;
    if (mapCoordinates && mapCoordinates.getBNGFromLatLng) {
        try {
            const bng = mapCoordinates.getBNGFromLatLng(lat, lng);
            return { easting: bng.easting, northing: bng.northing };
        }
        catch (e) {
            console.error('[StructureMoveDialog] mapCoordinates conversion failed:', e);
        }
    }
    // Try using the global proj4 library if available (less accurate - no OSTN15)
    const proj4 = window.proj4;
    if (proj4) {
        try {
            const [easting, northing] = proj4('EPSG:27700', [lng, lat]);
            console.log(`[StructureMoveDialog] LatLng→BNG: (${lat}, ${lng}) → (${easting}, ${northing}) [using proj4 - WARNING: No OSTN15 correction]`);
            // Apply cached grid corrections if available
            const gridSize = 6000;
            const gridX = Math.floor(easting / gridSize);
            const gridY = Math.floor(northing / gridSize);
            const gridKey = `${gridX}|${gridY} `;
            const storageKey = 'adjust27700Grid_2';
            try {
                const gridText = localStorage.getItem(storageKey);
                if (gridText) {
                    const adjustGrid = JSON.parse(gridText);
                    if (adjustGrid[gridKey]) {
                        const [deltaX, deltaY] = adjustGrid[gridKey].map((i) => i / 1000);
                        console.log(`[StructureMoveDialog] Applying grid correction: (${deltaX}, ${deltaY})`);
                        return { easting: easting + deltaX, northing: northing + deltaY };
                    }
                }
            }
            catch (e) {
                console.error('[StructureMoveDialog] Failed to apply grid corrections:', e);
            }
            return { easting, northing };
        }
        catch (e) {
            console.error('[StructureMoveDialog] proj4 conversion failed:', e);
        }
    }
    // Fallback approximate conversion (not accurate!)
    const easting = (lng + 8) * 71000;
    const northing = (lat - 49) * 111000;
    return { easting, northing };
}
// Async version that uses the /reproject endpoint for maximum accuracy
async function convertLatLngToBNGAsync(lat, lng) {
    try {
        const response = await fetch(`/reproject/${lng}/${lat}`);
        const data = await response.json();
        console.log(`[StructureMoveDialog] LatLng→BNG (server): (${lat}, ${lng}) → (${data.easting}, ${data.northing}) [using /reproject with OSTN15]`);
        return { easting: data.easting, northing: data.northing };
    }
    catch (e) {
        console.error('[StructureMoveDialog] Server reproject failed, using fallback:', e);
        return convertLatLngToBNG(lat, lng);
    }
}
